import { Resources } from 'constants/resources';

import { useQuery } from '@tanstack/react-query';
import { getOrderStatuses } from 'config/requests/orders';
import { useGetList, useRecordContext } from 'react-admin';
import { MenuItem } from 'shared/mui/MenuItem/MenuItem';
import { Popper } from 'shared/mui/Popper/Popper';
import { AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import {
  TextInputIntrospected,
  TextInputWithAliasIntrospected,
} from 'shared/react-admin/Inputs/TextInputs';
import { Select, SelectItem } from 'shared/react-admin/Selects/Select/Select';
import { Role, RoleCategory } from 'types/embedded/role';

import { BoxStyled } from './RolesForm.styled';
import { RolesFormDirection } from './RolesFormDirection';

type RolesFormProps = {
  defaultOrderStatuses?: string[];
};

const CustomPopper = (props: any) => <Popper {...props} popperOptions={{ placement: 'bottom' }} />;

export const RolesForm: React.FC<RolesFormProps> = ({ defaultOrderStatuses }) => {
  const record = useRecordContext<Role>();

  const { data: roleCategories } = useGetList<RoleCategory>(Resources.RoleCategories);
  const { data: orderStatuses } = useQuery({
    queryKey: ['new/order_statuses'],
    queryFn: getOrderStatuses,
  });

  const defaultOrderStatusesFiltered =
    defaultOrderStatuses &&
    orderStatuses?.filter(({ value }) => defaultOrderStatuses.find((status) => status === value));

  const roleCategoriesOptions = roleCategories?.map(({ id, value, description }) => ({
    value: id,
    label: value,
    description,
  }));

  const defaultRoleIcon = roleCategoriesOptions?.find(({ label }) => label === 'default');

  const selectedRoleIcon =
    roleCategoriesOptions?.find(({ value }) => value === record?.roleCategories?.['@id']) ||
    defaultRoleIcon;

  return (
    <BoxStyled>
      <TextInputIntrospected
        fullWidth
        helperText={false}
        label="Название"
        size="small"
        source="name"
        sx={{ gridColumn: ' 1 / span 3' }}
        variant="outlined"
      />
      {roleCategories && (
        <Select
          MenuProps={{
            PaperProps: {
              style: {
                width: '396px',
                height: '300px',
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
          defaultValue={selectedRoleIcon}
          items={roleCategoriesOptions}
          label="Иконка"
          renderValue={({ description }: SelectItem<string>) => (
            <RolesFormDirection description={description} />
          )}
          required
          shrink
          size="small"
          source="roleCategories"
        >
          {roleCategoriesOptions?.map(({ description, value, label }) => (
            <MenuItem key={value} value={value}>
              <RolesFormDirection description={description} value={label} />
            </MenuItem>
          ))}
        </Select>
      )}
      <TextInputWithAliasIntrospected
        fullWidth
        helperText={false}
        label="Алиас"
        size="small"
        source="alias"
        sx={{ gridColumn: ' 1 / span 2' }}
        transformValue={(value) => value.replace(/[0-9-]/g, '').replace(/^_+([a-zA-Z])/, '$1')}
        variant="outlined"
      />
      {orderStatuses ? (
        <AutocompleteMultiselectInput
          ListboxProps={{ sx: { height: '250px' } }}
          PopperComponent={CustomPopper}
          defaultValue={defaultOrderStatusesFiltered}
          fullWidth
          helperText={false}
          label="Статусы заказа"
          name="allowedStatusesEditing"
          optionText="description"
          optionValue="value"
          options={orderStatuses}
          sx={{ gridColumn: ' 3 / span 2' }}
        />
      ) : null}
    </BoxStyled>
  );
};
